import { add } from 'date-fns/add'
import { isValid } from 'date-fns/isValid'
import { isWithinInterval } from 'date-fns/isWithinInterval'
import { startOfDay } from 'date-fns/startOfDay'
import { sub } from 'date-fns/sub'
import _get from 'lodash/get'
import { z } from 'zod'

import * as schemaUtils from '@flows/configs/shared/schemaUtils'
import type { StepConfigParams } from '@flows/configs/types'
import { t } from '@flows/core/i18n'
import type { AnyStepConfiguration } from '@flows/core/types/stepTypes'
import { BaseSchema_PetHealthV1GetsafeDe } from '@flows/insurance/schemas'
import type { ProductKey } from '@flows/insurance/types/productKeys'
import { createSafeDate, toLocalDate } from '@flows/insurance/utils/dateUtils'

const getErrorMap = (): z.ZodErrorMap => (issue, ctx) => {
  switch (issue.code) {
    case z.ZodIssueCode.too_big:
      return { message: t('sharedFlows_petHealth:steps.pet_birthday.error.tooYoung') }
    case z.ZodIssueCode.too_small:
      return { message: t('sharedFlows_petHealth:steps.pet_birthday.error.tooOld') }
    default:
      return { message: ctx.defaultError }
  }
}

/**
 * The contract start date reaches this step in two shapes: the web flows keep it as a
 * `startDates` object per product, the app sends `effective_on` directly. Either can still be
 * unset here, because the start date is optional until checkout — in that case the contract
 * starts today, which is what the price endpoint assumes as well.
 */
const resolveEffectiveOn = (values: any, productKey: ProductKey): string => {
  const startDate = _get(values, ['startDates', productKey])
  const startDateFromParts = startDate
    ? createSafeDate(startDate.year, startDate.month, startDate.day)
    : undefined

  const effectiveOn =
    (isValid(startDateFromParts) ? startDateFromParts : undefined) ??
    toLocalDate(values.effective_on) ??
    startOfDay(new Date())

  return effectiveOn.toJSON()
}

const pet_birthday = (params: StepConfigParams) =>
  ({
    errorMap: getErrorMap(),
    schema: z.preprocess(
      (values: any) => ({
        ...values,
        effective_on: resolveEffectiveOn(values, params.productKey),
      }),
      schemaUtils
        .generateZodObjectFromPaths({
          [params.helperFunctions.getParamPath('pet_date_of_birth')]:
            BaseSchema_PetHealthV1GetsafeDe.shape.pet_date_of_birth,
          [params.helperFunctions.getParamPath('pet_age_at_contract_start')]:
            BaseSchema_PetHealthV1GetsafeDe.shape.pet_age_at_contract_start,
          effective_on: z.string(),
        })
        // we are extending the birth date schema with a custom validation
        // we are checking that the birth date does match with the age year gap to the starting date of the contract
        .refine(
          (values) => {
            const birthDate = new Date(
              _get(values, params.helperFunctions.getParamPath('pet_date_of_birth'))
            )
            birthDate.setHours(0)

            const petAge = Number(
              _get(values, params.helperFunctions.getParamPath('pet_age_at_contract_start'))
            )

            const minimumBirthday = add(sub(new Date(values.effective_on), { years: petAge + 1 }), {
              days: 1,
            })
            const maximumBirthday = sub(new Date(values.effective_on), { years: petAge })

            return isWithinInterval(birthDate, {
              start: minimumBirthday,
              end: maximumBirthday,
            })
          },
          () => ({
            path: [params.helperFunctions.getParamPath('pet_date_of_birth')],
            message: t('sharedFlows_petHealth:steps.pet_birthday.error.matching'),
          })
        )
    ),

    stepContent: [
      {
        type: 'title',
        text: {
          type: 'translatableText',
          translationKey: 'sharedFlows_petHealth:steps.pet_birthday.title',
          values: {
            pet_name: {
              type: 'dynamicValue',
              path: params.helperFunctions.getParamPath('pet_name'),
            },
          },
        },
      },
      {
        type: 'date',
        shouldTrackValues: true,
        name: params.helperFunctions.getParamPath('pet_date_of_birth'),
      },
      {
        type: 'infoBox',
        showInfoIcon: true,
        contentConfiguration: [
          {
            type: 'title',
            text: {
              type: 'translatableText',
              translationKey: 'sharedFlows_petHealth:steps.pet_birthday.infoBox.title',
            },
            isSmall: true,
          },
          {
            type: 'text',
            text: {
              type: 'translatableText',
              translationKey: 'sharedFlows_petHealth:steps.pet_birthday.infoBox.text',
            },
          },
        ],
      },
    ],
    subFlow: params.subFlow,
    nextStep: 'pet_gender',
  }) as const satisfies AnyStepConfiguration

export default pet_birthday
